import { MutableRefObject, useCallback, useRef } from "react"

import airbrake from "src/airbrake-notifier"

import { loadGeminiLivePcmCaptureWorklet } from "./helpers/geminiLiveAudioWorklets"
import { GeminiLiveSession } from "./helpers/geminiLiveSession"

import { MeetingAudioGraph } from "./useMeetingAudioGraph"

const useAudioCaptureWorklet = (
  geminiSessionRef: MutableRefObject<GeminiLiveSession | null>
) => {
  const captureNodeRef = useRef<AudioWorkletNode | null>(null)

  const setupCaptureWorklet = useCallback(
    async (
      graph: MeetingAudioGraph,
      onConnectionLost: VoidFunction
    ): Promise<void> => {
      await loadGeminiLivePcmCaptureWorklet(graph.context)
      const captureNode = new AudioWorkletNode(
        graph.context,
        "gemini-live-pcm-capture",
        { channelCount: 1 }
      )
      graph.source.connect(captureNode)
      captureNode.connect(graph.context.destination) // required for worklet process() to be called

      captureNode.port.onmessage = (workletEvent: MessageEvent) => {
        const audioChunk = workletEvent.data
        if (!audioChunk.pcm?.byteLength) return
        geminiSessionRef.current?.send(audioChunk.pcm, audioChunk.sampleRate)
      }
      captureNode.onprocessorerror = error => {
        airbrake.notify({
          message: "Error from inside Gemini capture worklet",
          params: { name: "useAudioCaptureWorklet" },
          error,
        })
        onConnectionLost()
      }
      captureNodeRef.current = captureNode
    },
    [geminiSessionRef]
  )

  const teardownCaptureWorklet = useCallback(() => {
    const captureNode = captureNodeRef.current
    if (!captureNode) return
    captureNode.port.onmessage = null
    captureNode.disconnect()
    captureNodeRef.current = null
  }, [])

  return { setupCaptureWorklet, teardownCaptureWorklet }
}

export default useAudioCaptureWorklet
